<template>
  <div class="payout-tax-list">
    <section class="payout-tax-list__item payout-tax">
      <h2>Payout Method</h2>
      <validation-observer v-slot="{ handleSubmit, reset }" ref="payoutObserver">
        <form @reset.prevent="setSectionNotEditable(reset)"
              @submit.prevent="handleSubmit(submitPayoutMethod)"
        >
          <div class="columns">
          <div class="column">
                  <field v-model.trim="getPayoutMethod"
                         type="list"
                         :listItems="payoutMethods"
                         :editable="isEditable()"
                         @change.native="changeField"></field>
            <div class="columns">
              <div class="column">
                <field name="Payee Name"
                       v-if="!isDirectDeposit"
                       v-model.trim="getPayeeName"></field>
                <field name="Address"
                       v-if="!isDirectDeposit"
                       v-model.trim="getCompanyAddress"></field>
                <div v-if="isDirectDeposit">
                  <field name="Account Holder's Name*"
                         v-model.trim="accHolderName"
                         :editable="isEditable()"
                         rules="required|maxLength:255"></field>
                </div>
                <div v-if="isDirectDeposit">
                  <validation-provider
                    v-slot="{ errors }"
 :rules="routingNumberNew || (!routingNumberNew && !routingNumber) ? 'required|routing_number' : ''"
                    v-if="isDirectDeposit"
                    mode="lazy">
                    <b-field :type="{'is-danger': errors.length }" :message="errors"
                             label="Routing Number*"
                             class="personal-info__field"
                             custom-class="personal-info__field-label">
                      <template v-if="isEditable()">
                        <div class="control" :class="{'has-icons-right': errors.length}">
                          <input
                            v-model="routingNumberNew"
                            :placeholder="routingNumber ? 'Keep number unchanged' : ''"
                            class="input"
                            v-mask="'#########'"
                            :class="{'is-danger': errors.length}">
                          <span v-if="errors.length" class="icon is-right has-text-danger">
                            <i class="mdi mdi-alert-circle mdi-24px"></i>
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <slot name="raw">{{ routingNumber }}</slot>
                      </template>
                    </b-field>
                  </validation-provider>
                </div>
              </div>
              <div class="column">
                <field name="Bank Name*"
                       v-if="isDirectDeposit"
                       v-model.trim="bankName"
                       :editable="isEditable()"
                       rules="required|maxLength:255"></field>
                <validation-provider
                  v-slot="{ errors }"
             :rules="accNumberNew || (!accNumberNew && !accNumber) ? 'required|account_number' : ''"
                  v-if="isDirectDeposit">
                  <b-field :type="{'is-danger': errors.length }" :message="errors"
                           label="Account Number*"
                           class="personal-info__field"
                           custom-class="personal-info__field-label">
                    <template v-if="isEditable()">
                      <div class="control" :class="{'has-icons-right': errors.length}">
                        <input
                          v-model="accNumberNew"
                          :placeholder="accNumber ? 'Keep number unchanged' : ''"
                          class="input"
                          v-mask="'#################'"
                          :class="{'is-danger': errors.length}"
                        >
                        <span v-if="errors.length" class="icon is-right has-text-danger">
                            <i class="mdi mdi-alert-circle mdi-24px"></i>
                          </span>
                      </div>
                    </template>
                    <template v-else>
                      <slot name="raw">{{ accNumber }}</slot>
                    </template>
                  </b-field>
                </validation-provider>
              </div>
              <div class="column is-4" v-if="isDirectDeposit && editable">
                <img :src="getCheckPicture()">
              </div>
            </div>
          </div>
            <div class="column is-3">
              <div class="columns">
                <div class="column">
                  <template v-if="isEditable()">
                    <div class="buttons is-pulled-right">
                      <button class="button" type="reset">
                        Cancel
                      </button>
                      <button class="button is-success" type="submit">Submit</button>
                    </div>
                  </template>
                  <template v-else>
                    <div class="buttons is-pulled-right">
                      <b-button class="is-pulled-right" type="is-success"
                                @click="setSectionEditable()">Edit</b-button>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </section>
  </div>
</template>

<script>
import Field from '@/components/Field.vue';
import { contractorPayoutChange, contractorPayoutTax } from '@/api/contractor';
import { partnerPayoutChange, partnerPayoutTax } from '@/api/partner';
import { mapMutations } from 'vuex';
import * as MUTATION from '@/store/mutations';
import { VueMaskDirective } from 'v-mask';
import { isEmpty } from 'lodash';

export default {
  name: 'PayoutMethod',
  components: {
    Field,
  },
  directives: { mask: VueMaskDirective },
  data: () => ({
    id: null,
    contractor: null,
    payoutMethod: 'Direct Deposit',
    accHolderName: null,
    routingNumber: null,
    routingNumberNew: null,
    bankName: null,
    accNumber: null,
    accNumberNew: null,
    editable: false,
    payoutMethods: ['Direct Deposit (ACH)'],
    fields: ['payoutMethod', 'accHolderName', 'bankName', 'accNumber', 'routingNumber'],
  }),
  props: {
    editImmediately: {
      type: Boolean,
      default: false,
    },
  },
  activated() {
    this.loadAsyncData();
    if (this.editImmediately) {
      this.setSectionEditable();
    }
  },
  deactivated() {
    if (this.editImmediately) {
      this.setSectionNotEditable();
    }
  },
  computed: {
    entityType() {
      if (this.$store.state.contractor && this.$store.state.contractor.id) {
        return 'contractor';
      }
      if (this.$store.state.partner && this.$store.state.partner.id) {
        return 'partner';
      }
      return null;
    },
    entityId() {
      if (this.entityType === 'partner') {
        return this.$store.state.partner.id;
      }
      if (this.entityType === 'contractor') {
        return this.$store.state.contractor.id;
      }
      return null;
    },
    getPayeeName() {
      if (this.entityType === 'partner') {
        const { partner } = this.$store.state;
        return `${partner.firstName} ${partner.lastName}`;
      }
      return `${this.$store.state.user.firstname} ${this.$store.state.user.lastname}`;
    },
    getCompanyAddress() {
      return this.$store.state[this.entityType || 'contractor'].companyAddress;
    },
    getPayoutMethod: {
      get() {
        return this.payoutMethod === 'Direct Deposit' ? `${this.payoutMethod} (ACH)` : this.payoutMethod;
      },
      set(val) {
        this.payoutMethod = val.indexOf('Direct') !== -1 ? 'Direct Deposit' : val;
      },
    },
    setPayoutMethod(val) {
      return val;
    },
    isDirectDeposit() {
      return this.payoutMethod === 'Direct Deposit';
    },
  },

  methods: {
    ...mapMutations({ preloader: MUTATION.SET_PRELOADER }),

    /**
     * Check if section is editable
     * @returns {boolean}
     */
    isEditable() {
      return this.editable;
    },

    setSectionEditable() {
      this.editable = true;
      this.routingNumberNew = null;
      this.accNumberNew = null;
    },


    /**
     * @param reset
     */
    setSectionNotEditable(reset = null) {
      this.editable = false;
      if (typeof reset === 'function') reset();
      this.loadAsyncData();
      this.routingNumberNew = this.routingNumber;
      this.accNumberNew = this.accNumber;
    },

    /**
     * Submit section
     */
    submitPayoutMethod() {
      let payload = null;
      const fields = this.fields.slice();

      if (this.payoutMethod === 'Direct Deposit') {
        if (this.routingNumberNew) {
          this.routingNumber = this.routingNumberNew;
        } else {
          fields.splice(fields.findIndex((item) => item === 'routingNumber'), 1);
        }
        if (this.accNumberNew) {
          this.accNumber = this.accNumberNew;
        } else {
          fields.splice(fields.findIndex((item) => item === 'accNumber'), 1);
        }
      } else {
        this.routingNumber = null;
        this.accNumber = null;
      }

      payload = this.lodash.pick(this, fields);

      const payoutChange = this.entityType === 'partner' ? partnerPayoutChange : contractorPayoutChange;

      this.preloader(true);
      payoutChange({ id: this.entityId, ...payload })
        .then((response) => {
          this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success' });
          this.setSectionNotEditable();
          if (response.data) {
            this.routingNumber = response.data.routingNumber;
            this.accNumber = response.data.accNumber;
            this.routingNumberNew = null;
            this.accNumberNew = null;
          }
        })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger' }))
        .finally(() => this.preloader(false));
    },
    changeField() {
      this.resetFields();
    },
    resetFields() {
      this.accNumberNew = null;
      this.accHolderName = null;
      this.bankName = null;
      this.routingNumberNew = null;
      this.routingNumber = null;
      this.accNumber = null;
      this.payoutMethod = null;
      if (this.$refs.payoutObserver.flags.changed) this.$refs.payoutObserver.reset();
    },
    loadAsyncData() {
      if (this.entityId != null) {
        const payoutTax = this.entityType === 'partner' ? partnerPayoutTax : contractorPayoutTax;
        payoutTax(this.entityId).then((response) => {
          if (!isEmpty(response.data)) {
            Object.keys(response.data).forEach((key) => {
              if (Object.prototype.hasOwnProperty.call(this, [key])) {
                this[key] = response.data[key];
              }
            });
          } else {
            this.resetFields();
            this.payoutMethod = 'Direct Deposit';
          }
        });
      } else {
        this.resetFields();
      }
    },
    getCheckPicture() {
      return '/img/payoutTax/check.png';
    },
  },
};
</script>

<style scoped lang="stylus">
form > .columns > .column > span:first-child
  font-weight bold
.columns >>> .column
  min-width 180px
.payout-tax
  color #293033
.payout-tax >>> .input
  height 48px
  color #293033
.payout-tax >>> .select:not(.is-multiple)
  height 48px
  select
    height 48px
    color #293033
>>> .personal-info__field-label
  color #8A9499
@media screen and (max-width 768px)
  .payout-tax
    position relative
    .buttons.is-pulled-right
      position absolute
      top 0
      right 0
@media screen and (max-width 480px)
  .payout-tax
    .buttons.is-pulled-right
      position static
      float none !important
      justify-content flex-end
</style>
